import {
  type InfiniteData,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { retryNetworkErrors } from '@/lib/react-query/queryClient';
import {
  isRateLimited,
  MAX_RATE_LIMIT_RETRIES,
  rateLimitDelayMs,
} from '@/modules/slack-notification-channel/channelPages';
import { endpoints } from '@/modules/slack-notification-channel/constants';
import { slackNotificationChannelQueryKeys } from '@/modules/slack-notification-channel/queries/queryKeys';
import type { SlackChannelPageApi } from '@/modules/slack-notification-channel/types';

type Pages = InfiniteData<SlackChannelPageApi, string | null>;

interface SelectableSlackChannelsOptions extends Pick<
  UseInfiniteQueryOptions<
    SlackChannelPageApi,
    Error,
    Pages,
    readonly unknown[],
    string | null
  >,
  'enabled' | 'refetchOnWindowFocus'
> {
  /**
   * Gives up on the first page after this many milliseconds, rate-limit waits
   * included, so the caller can offer a way on. Later pages keep loading.
   */
  firstPageBudgetMs?: number;
}

const fetchPage = async (cursor: string | null, signal: AbortSignal) => {
  const { data } = await fetchClient.GET(endpoints.availableChannelsPage, {
    params: { query: cursor ? { cursor } : {} },
    signal,
  });
  return data ?? { channels: [], nextCursor: null };
};

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });

/** The first page within a budget: a 429 is waited out only while the wait fits. */
const fetchFirstPageWithin = async (budgetMs: number, signal: AbortSignal) => {
  const giveUpAt = Date.now() + budgetMs;
  const deadline = AbortSignal.any([signal, AbortSignal.timeout(budgetMs)]);

  for (;;) {
    try {
      return await fetchPage(null, deadline);
    } catch (error) {
      if (!isRateLimited(error)) throw error;
      const delay = rateLimitDelayMs(error);
      if (Date.now() + delay >= giveUpAt) {
        // Not a 429 any more, so the query's rate-limit retry leaves it alone.
        throw new DOMException('Slack channels timed out.', 'TimeoutError');
      }
      await wait(delay, deadline);
    }
  }
};

/**
 * The Slack channels the bot can post to in the current user's workspace.
 * Each request is one Slack call; the next page loads automatically after
 * each success, so the list fills in and search covers the whole workspace
 * once loading ends. A rate-limited page is retried after Slack's delay.
 */
export const useSelectableSlackChannelsQuery = ({
  firstPageBudgetMs,
  ...options
}: SelectableSlackChannelsOptions = {}) => {
  const query = useInfiniteQuery({
    queryKey: slackNotificationChannelQueryKeys.availableChannelPages(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      pageParam === null && firstPageBudgetMs
        ? fetchFirstPageWithin(firstPageBudgetMs, signal)
        : fetchPage(pageParam, signal),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: (failureCount, error) =>
      isRateLimited(error)
        ? failureCount < MAX_RATE_LIMIT_RETRIES
        : retryNetworkErrors(failureCount, error),
    retryDelay: (failureCount, error) =>
      isRateLimited(error)
        ? rateLimitDelayMs(error)
        : Math.min(1000 * 2 ** failureCount, 30_000),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

  const { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage } =
    query;
  useEffect(() => {
    // One page at a time: a refetch or a failed page pauses loading.
    if (hasNextPage && !isFetching && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetching, isFetchNextPageError, fetchNextPage]);

  const pages = query.data?.pages;
  return {
    channels: pages?.flatMap((page) => page.channels) ?? [],
    pages,
    /** Nothing loaded yet. */
    isPending: query.isPending,
    /** The first page failed; later failures keep what loaded. */
    isError: query.isError && !query.data,
    isFetching,
    /** More pages are on their way. */
    isLoadingMore: hasNextPage && !isFetchNextPageError,
    /** A later page gave up after its retries; what loaded stays usable. */
    hasLoadMoreError: isFetchNextPageError,
    retry: () =>
      isFetchNextPageError ? void fetchNextPage() : void query.refetch(),
  };
};
